// Generated by GitHub Copilot
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaSync, FaPlus, FaFilter, FaSearch, FaTimes, FaEdit, FaTrash } from "react-icons/fa";
import ConfirmationModal from '../Modern/ConfirmationModal';
import RefreshIndicator from '../Modern/Incidents/components/RefreshIndicator';
import usePolling from '../../hooks/usePolling';
import { POLLING_INTERVALS } from '../../config/polling';
import { validateSearchTerm } from '../../utils/validation';
import { trackDeliveryStreamOperation } from '../../utils/modernUITracking';
import styles from "./DeliveryStreamsList.module.css";

// Generated by GitHub Copilot
// Skeleton loader component for loading states
function SkeletonLoader() {
  return (
    <div className={styles.skeleton}>
      {[...Array(5)].map((_, i) => (
        <div key={i} className={styles.skeletonRow}>
          <div className={styles.skeletonCell} style={{ flex: 2 }}></div>
          <div className={styles.skeletonCell} style={{ flex: 1 }}></div>
          <div className={styles.skeletonCell} style={{ flex: 0.5 }}></div>
        </div>
      ))}
    </div>
  );
}

function DeliveryStreamsList({ user }) {
  // Generated by GitHub Copilot
  const navigate = useNavigate();
  const [deliveryStreams, setDeliveryStreams] = useState([]);
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sequence, setSequence] = useState(0);
  const abortControllerRef = useRef(null);

  // Background refresh state
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Generated by GitHub Copilot
  // Search-related state
  const [searchTerm, setSearchTerm] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [showFilterBox, setShowFilterBox] = useState(false);

  // Generated by GitHub Copilot
  // Commander search state
  const [commanderSearchTerm, setCommanderSearchTerm] = useState("");
  const [commanderSearchLoading, setCommanderSearchLoading] = useState(false);
  const [commanderSearchError, setCommanderSearchError] = useState("");
  const [isCommanderSearchActive, setIsCommanderSearchActive] = useState(false);

  // Generated by GitHub Copilot
  // Leadership responder search state
  const [leadershipSearchTerm, setLeadershipSearchTerm] = useState("");
  const [leadershipSearchLoading, setLeadershipSearchLoading] = useState(false);
  const [leadershipSearchError, setLeadershipSearchError] = useState("");
  const [isLeadershipSearchActive, setIsLeadershipSearchActive] =
    useState(false);

  // Generated by GitHub Copilot
  // Search mode state for mutually exclusive search
  const [searchMode, setSearchMode] = useState("product"); // 'product' | 'commander' | 'leadership'

  // Generated by GitHub Copilot
  // Modal state for delete confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [streamToDelete, setStreamToDelete] = useState(null);

  // Generated by GitHub Copilot
  // Debounce hook for search optimization
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [debouncedCommanderSearchTerm, setDebouncedCommanderSearchTerm] =
    useState("");
  const [debouncedLeadershipSearchTerm, setDebouncedLeadershipSearchTerm] =
    useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Generated by GitHub Copilot
  // Debounce hook for commander search optimization
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCommanderSearchTerm(commanderSearchTerm);
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timer);
  }, [commanderSearchTerm]);

  // Generated by GitHub Copilot
  // Debounce hook for leadership search optimization
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedLeadershipSearchTerm(leadershipSearchTerm);
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timer);
  }, [leadershipSearchTerm]);

  // Generated by GitHub Copilot
  // Check if user has configuration privilege for read-write access
  const hasConfigurationPrivilege =
    user && user.privileges && user.privileges.configuration;

  // Generated by GitHub Copilot
  // Check if user has any access to view delivery streams (configuration or editor privileges)
  const canViewDeliveryStreams =
    user &&
    user.privileges &&
    (user.privileges.configuration ||
      user.privileges.editor ||
      user.privileges.deity);

  // Generated by GitHub Copilot
  // Refresh delivery streams list and clear all searches
  const refreshDeliveryStreams = useCallback(() => {
    setSearchTerm("");
    setSearchError("");
    setIsSearchActive(false);
    setCommanderSearchTerm("");
    setCommanderSearchError("");
    setIsCommanderSearchActive(false);
    setSequence((prev) => prev + 1);
  }, []);

  // Generated by GitHub Copilot
  // Fetch delivery streams data with optional search filters
  const fetchDeliveryStreams = useCallback(
    async (productFilter = "", commanderFilter = "", isBackgroundRefresh = false) => {
      if (!canViewDeliveryStreams) {
        setLoading(false);
        return;
      }

      const isProductSearching = productFilter.trim() !== "";
      const isCommanderSearching = commanderFilter.trim() !== "";
      const isAnySearching = isProductSearching || isCommanderSearching;

      if (isAnySearching) {
        if (isProductSearching) {
          if (isBackgroundRefresh) {
            setRefreshing(true);
          } else {
            setSearchLoading(true);
            setSearchError("");
          }
        }
        if (isCommanderSearching) {
          if (isBackgroundRefresh) {
            setRefreshing(true);
          } else {
            setCommanderSearchLoading(true);
            setCommanderSearchError("");
          }
        }
      } else {
        if (isBackgroundRefresh) {
          setRefreshing(true);
        } else {
          setLoading(true);
          setError("");
        }
      }

      try {
        // VALIDATE search terms before API call
        let validatedProductFilter = '';
        let validatedCommanderFilter = '';

        if (isProductSearching) {
          try {
            validatedProductFilter = validateSearchTerm(productFilter, 100);
          } catch (validationErr) {
            setSearchError(validationErr.message);
            setSearchLoading(false);
            return;
          }
        }

        if (isCommanderSearching) {
          try {
            validatedCommanderFilter = validateSearchTerm(commanderFilter, 100);
          } catch (validationErr) {
            setCommanderSearchError(validationErr.message);
            setCommanderSearchLoading(false);
            return;
          }
        }

        // Cancel previous request if exists
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        // Create new abort controller for this request
        abortControllerRef.current = new AbortController();

        // Build URL with optional filters
        let url = "/api/v1/library/delivery";
        const queryParams = [];

        if (isProductSearching) {
          queryParams.push(
            `product=${encodeURIComponent(validatedProductFilter)}`
          );
        }

        if (isCommanderSearching) {
          queryParams.push(
            `commander=${encodeURIComponent(validatedCommanderFilter)}`
          );
        }

        if (queryParams.length > 0) {
          url += `?${queryParams.join("&")}`;
        }

        const res = await fetch(url, { signal: abortControllerRef.current.signal });
        if (!res.ok) {
          throw new Error("Failed to fetch delivery streams");
        }

        const defs = await res.json();

        if (!defs || defs.length === 0) {
          setDefinitions([]);
          setDeliveryStreams([]);
          if (isAnySearching) {
            if (isProductSearching) {
              setSearchLoading(false);
            }
            if (isCommanderSearching) {
              setCommanderSearchLoading(false);
            }
          } else {
            setLoading(false);
          }
          return;
        }

        const definitionRows = defs.map((def) => {
          const defLink = "/configuration/deliverystream/" + def.id;
          return (
            <tr
              key={def.id}
              className={`${styles.tableRow} ${!hasConfigurationPrivilege ? styles.readOnly : ""}`}
            >
              <td className={styles.tableCell}>
                {/* Generated by GitHub Copilot */}
                {canViewDeliveryStreams ? (
                  <Link
                    to={defLink}
                    className={styles.link}
                    title={
                      hasConfigurationPrivilege
                        ? "Edit delivery stream"
                        : "View delivery stream (read-only)"
                    }
                  >
                    {def.name}
                  </Link>
                ) : (
                  <span
                    className={styles.readOnlyText}
                    title="Access denied - requires viewing privileges"
                  >
                    {def.name}
                  </span>
                )}
              </td>
              <td className={styles.tableCell}>{def.portfolio}</td>
              {hasConfigurationPrivilege && (
                <td className={styles.tableCell} data-label="Actions">
                  <div className={styles.actionButtons}>
                    <button
                      className={styles.iconButton}
                      onClick={() => navigate(defLink)}
                      title="Edit delivery stream"
                      aria-label={`Edit ${def.name}`}
                    >
                      <FaEdit />
                    </button>
                    <button
                      className={`${styles.iconButton} ${styles.iconButtonDanger}`}
                      onClick={() => { setStreamToDelete(def); setShowDeleteModal(true); }}
                      title="Delete delivery stream"
                      aria-label={`Delete ${def.name}`}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </td>
              )}
            </tr>
          );
        });

        setDeliveryStreams(defs);
        setDefinitions(definitionRows);
        setLastUpdated(new Date());
        setIsInitialLoad(false);
      } catch (err) {
        // Don't show error for aborted requests
        if (err.name === 'AbortError') {
          return;
        }

        console.error("Error loading delivery streams:", err);
        const errorMessage =
          "Failed to load delivery streams. Please try again.";

        // During background refresh, log but keep old data
        if (isBackgroundRefresh) {
          console.warn("Background refresh failed, keeping existing data:", errorMessage);
        } else {
          if (isAnySearching) {
            if (isProductSearching) {
              setSearchError(errorMessage);
            }
            if (isCommanderSearching) {
              setCommanderSearchError(errorMessage);
            }
          } else {
            setError(errorMessage);
          }
        }
      } finally {
        if (isBackgroundRefresh) {
          setRefreshing(false);
        } else {
          if (isAnySearching) {
            if (isProductSearching) {
              setSearchLoading(false);
            }
            if (isCommanderSearching) {
              setCommanderSearchLoading(false);
            }
          } else {
            setLoading(false);
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canViewDeliveryStreams, hasConfigurationPrivilege, navigate]
    // handleDelete is intentionally omitted to avoid circular dependency - it's stable and used only in JSX
  );

  // Generated by GitHub Copilot
  // Load delivery streams data
  useEffect(() => {
    fetchDeliveryStreams();
  }, [
    sequence,
    canViewDeliveryStreams,
    hasConfigurationPrivilege,
    fetchDeliveryStreams,
  ]);

  // Background refresh polling
  usePolling(
    () => fetchDeliveryStreams("", "", true),
    POLLING_INTERVALS.DELIVERY_STREAMS,
    {
      enabled: !isInitialLoad && !loading,
      pauseWhenHidden: true
    }
  );

  // Generated by GitHub Copilot
  // Automatic search when debounced product term changes
  useEffect(() => {
    if (debouncedSearchTerm.trim()) {
      setIsSearchActive(true);
      fetchDeliveryStreams(debouncedSearchTerm, debouncedCommanderSearchTerm);

      // Track product search
      trackDeliveryStreamOperation('Search', {
        searchType: 'product',
        searchTerm: debouncedSearchTerm,
        resultCount: deliveryStreams.length
      });
    } else if (isSearchActive && !debouncedCommanderSearchTerm.trim()) {
      // Only refresh if we were previously searching and no commander search is active
      setIsSearchActive(false);
      refreshDeliveryStreams();
    } else if (
      !debouncedSearchTerm.trim() &&
      debouncedCommanderSearchTerm.trim()
    ) {
      // Product search cleared but commander search is active
      setIsSearchActive(false);
      fetchDeliveryStreams("", debouncedCommanderSearchTerm);
    }
  }, [
    debouncedSearchTerm,
    debouncedCommanderSearchTerm,
    fetchDeliveryStreams,
    isSearchActive,
    refreshDeliveryStreams,
    deliveryStreams.length,
  ]);

  // Generated by GitHub Copilot
  // Automatic search when debounced commander term changes
  useEffect(() => {
    if (debouncedCommanderSearchTerm.trim()) {
      setIsCommanderSearchActive(true);
      fetchDeliveryStreams(debouncedSearchTerm, debouncedCommanderSearchTerm);

      // Track commander search
      trackDeliveryStreamOperation('Search', {
        searchType: 'commander',
        searchTerm: debouncedCommanderSearchTerm,
        resultCount: deliveryStreams.length
      });
    } else if (isCommanderSearchActive && !debouncedSearchTerm.trim()) {
      // Only refresh if we were previously searching and no product search is active
      setIsCommanderSearchActive(false);
      refreshDeliveryStreams();
    } else if (
      !debouncedCommanderSearchTerm.trim() &&
      debouncedSearchTerm.trim()
    ) {
      // Commander search cleared but product search is active
      setIsCommanderSearchActive(false);
      fetchDeliveryStreams(debouncedSearchTerm, "");
    }
  }, [
    debouncedCommanderSearchTerm,
    debouncedSearchTerm,
    fetchDeliveryStreams,
    isCommanderSearchActive,
    refreshDeliveryStreams,
    deliveryStreams.length,
  ]);

  // Generated by GitHub Copilot
  // Perform leadership responder search using the new API endpoint
  const performLeadershipSearch = useCallback(
    async (searchTerm) => {
      if (!canViewDeliveryStreams || !searchTerm.trim()) {
        return;
      }

      setLeadershipSearchLoading(true);
      setLeadershipSearchError("");

      try {
        // VALIDATE search term before API call
        const validatedTerm = validateSearchTerm(searchTerm, 100);

        const response = await fetch(
          `/api/search/leadership-responders?search=${encodeURIComponent(
            validatedTerm
          )}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(
            `Search failed: ${response.status} ${response.statusText}`
          );
        }

        const data = await response.json();

        // Transform leadership search results into delivery stream format
        const transformedStreams = data.results.map((result) => ({
          id: result.delivery_stream.id,
          name: result.delivery_stream.name,
          portfolio: result.delivery_stream.portfolio,
          // Add leadership search specific data for UI enhancement
          _leadershipSearchData: {
            matchedWorkflows: result.matched_workflows,
            searchTerm: data.query,
          },
        }));

        // Create UI rows for the transformed streams
        const streamRows = transformedStreams.map((stream) => {
          const streamLink = `/configuration/deliverystream/${stream.id}`;
          return (
            <tr
              key={stream.id}
              className={`${styles.tableRow} ${!hasConfigurationPrivilege ? styles.readOnly : ""}`}
            >
              <td className={styles.tableCell}>
                {canViewDeliveryStreams ? (
                  <Link
                    to={streamLink}
                    className={styles.link}
                    title={
                      hasConfigurationPrivilege
                        ? "Edit delivery stream"
                        : "View delivery stream (read-only)"
                    }
                  >
                    {stream.name}
                  </Link>
                ) : (
                  <span
                    className={styles.readOnlyText}
                    title="Access denied - requires viewing privileges"
                  >
                    {stream.name}
                  </span>
                )}
                {/* Show matched workflows for context */}
                {stream._leadershipSearchData && (
                  <div className={styles.workflowInfo}>
                    Found in{" "}
                    {stream._leadershipSearchData.matchedWorkflows.length}{" "}
                    workflow
                    {stream._leadershipSearchData.matchedWorkflows.length !==
                    1
                      ? "s"
                      : ""}
                    :&nbsp;
                    {stream._leadershipSearchData.matchedWorkflows
                      .slice(0, 2)
                      .map((wf) => wf.workflow_name)
                      .join(", ")}
                    {stream._leadershipSearchData.matchedWorkflows.length >
                      2 &&
                      ` (+${
                        stream._leadershipSearchData.matchedWorkflows.length -
                        2
                      } more)`}
                  </div>
                )}
              </td>
              <td className={styles.tableCell}>{stream.portfolio}</td>
              {hasConfigurationPrivilege && (
                <td className={styles.tableCell} data-label="Actions">
                  <div className={styles.actionButtons}>
                    <button
                      className={styles.iconButton}
                      onClick={() => navigate(streamLink)}
                      title="Edit delivery stream"
                      aria-label={`Edit ${stream.name}`}
                    >
                      <FaEdit />
                    </button>
                    <button
                      className={`${styles.iconButton} ${styles.iconButtonDanger}`}
                      onClick={() => handleDelete(stream)}
                      title="Delete delivery stream"
                      aria-label={`Delete ${stream.name}`}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </td>
              )}
            </tr>
          );
        });

        setDeliveryStreams(transformedStreams);
        setDefinitions(streamRows);
        setSequence((prev) => prev + 1);
        setIsLeadershipSearchActive(true);
      } catch (err) {
        console.error("Leadership search failed:", err);

        // Handle validation errors
        if (err.message?.includes('Search term')) {
          setLeadershipSearchError(err.message);
        } else {
          setLeadershipSearchError(
            "Failed to search leadership responders. Please try again."
          );
        }
      } finally {
        setLeadershipSearchLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canViewDeliveryStreams, hasConfigurationPrivilege, navigate]
    // handleDelete is intentionally omitted to avoid circular dependency - it's stable and used only in JSX
  );

  // Generated by GitHub Copilot
  // Automatic leadership search when debounced term changes
  useEffect(() => {
    if (debouncedLeadershipSearchTerm.trim()) {
      performLeadershipSearch(debouncedLeadershipSearchTerm);

      // Track leadership search
      trackDeliveryStreamOperation('Search', {
        searchType: 'leadership',
        searchTerm: debouncedLeadershipSearchTerm,
        resultCount: deliveryStreams.length
      });
    } else if (isLeadershipSearchActive) {
      // Clear leadership search and show all results
      setIsLeadershipSearchActive(false);
      refreshDeliveryStreams();
    }
  }, [
    debouncedLeadershipSearchTerm,
    performLeadershipSearch,
    isLeadershipSearchActive,
    refreshDeliveryStreams,
    deliveryStreams.length,
  ]);

  // Generated by GitHub Copilot
  // Handle delete with confirmation
  const handleDelete = useCallback(async () => {
    if (!streamToDelete) return;

    const stream = streamToDelete;
    setShowDeleteModal(false);
    setStreamToDelete(null);

    try {
      const response = await fetch(`/api/v1/library/delivery/${stream.id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP error ${response.status}`);
      }

      // Track successful deletion
      trackDeliveryStreamOperation('Delete', {
        streamId: stream.id,
        streamName: stream.name,
        success: true
      });

      // Success - refresh the list
      refreshDeliveryStreams();
    } catch (err) {
      console.error("Delete error:", err);

      // Track failed deletion
      trackDeliveryStreamOperation('Delete', {
        streamId: stream.id,
        streamName: stream.name,
        success: false,
        error: err.message
      });

      setError(err.message || "Failed to delete delivery stream. Please try again.");
    }
  }, [streamToDelete, refreshDeliveryStreams]);

  // Generated by GitHub Copilot
  // Handle creating new delivery stream
  const createNewDeliveryStream = () => {
    // Track create button click
    trackDeliveryStreamOperation('Create', {
      action: 'button_click'
    });

    // Navigate to new delivery stream creation page
    // For now, keep the existing individual delivery stream editing functionality
    navigate("/configuration/deliverystream/new");
  };

  // Generated by GitHub Copilot
  // Handle manual search (button click or Enter key)
  const handleSearch = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
      }

      // Respect current search mode for Enter key and button clicks
      if (searchMode === "product") {
        if (searchTerm.trim()) {
          setIsSearchActive(true);
          fetchDeliveryStreams(searchTerm, "");
        } else {
          refreshDeliveryStreams();
        }
      } else if (searchMode === "commander") {
        if (commanderSearchTerm.trim()) {
          setIsCommanderSearchActive(true);
          fetchDeliveryStreams("", commanderSearchTerm);
        } else {
          refreshDeliveryStreams();
        }
      }
    },
    [
      searchMode,
      searchTerm,
      commanderSearchTerm,
      fetchDeliveryStreams,
      refreshDeliveryStreams,
    ]
  );

  // Generated by GitHub Copilot
  // Handle search input change with enhanced UX
  const handleSearchInputChange = useCallback(
    (e) => {
      const value = e.target.value;
      setSearchTerm(value);

      // Clear search error when user starts typing
      if (searchError) {
        setSearchError("");
      }

      // If user clears the input completely, immediately show all results if no commander search
      if (
        value.trim() === "" &&
        isSearchActive &&
        !commanderSearchTerm.trim()
      ) {
        setIsSearchActive(false);
        refreshDeliveryStreams();
      }
    },
    [searchError, isSearchActive, commanderSearchTerm, refreshDeliveryStreams]
  );

  // Generated by GitHub Copilot
  // Handle commander search input change
  const handleCommanderSearchInputChange = useCallback(
    (e) => {
      const value = e.target.value;
      setCommanderSearchTerm(value);

      // Clear commander search error when user starts typing
      if (commanderSearchError) {
        setCommanderSearchError("");
      }

      // If user clears the input completely, immediately show all results if no product search
      if (
        value.trim() === "" &&
        isCommanderSearchActive &&
        !searchTerm.trim()
      ) {
        setIsCommanderSearchActive(false);
        refreshDeliveryStreams();
      }
    },
    [
      commanderSearchError,
      isCommanderSearchActive,
      searchTerm,
      refreshDeliveryStreams,
    ]
  );

  // Generated by GitHub Copilot
  // Handle leadership search input change
  const handleLeadershipSearchInputChange = useCallback(
    (e) => {
      const value = e.target.value;
      setLeadershipSearchTerm(value);

      // Clear leadership search error when user starts typing
      if (leadershipSearchError) {
        setLeadershipSearchError("");
      }

      // If user clears the input completely, immediately show all results
      if (value.trim() === "" && isLeadershipSearchActive) {
        setIsLeadershipSearchActive(false);
        refreshDeliveryStreams();
      }
    },
    [leadershipSearchError, isLeadershipSearchActive, refreshDeliveryStreams]
  );

  // Generated by GitHub Copilot
  // Handle search mode switching (mutually exclusive)
  const handleSearchModeChange = useCallback(
    (newMode) => {
      if (newMode === searchMode) return;

      // Clear all inactive searches when switching modes
      if (newMode === "product") {
        setCommanderSearchTerm("");
        setCommanderSearchError("");
        setIsCommanderSearchActive(false);
        setLeadershipSearchTerm("");
        setLeadershipSearchError("");
        setIsLeadershipSearchActive(false);
      } else if (newMode === "commander") {
        setSearchTerm("");
        setSearchError("");
        setIsSearchActive(false);
        setLeadershipSearchTerm("");
        setLeadershipSearchError("");
        setIsLeadershipSearchActive(false);
      } else if (newMode === "leadership") {
        setSearchTerm("");
        setSearchError("");
        setIsSearchActive(false);
        setCommanderSearchTerm("");
        setCommanderSearchError("");
        setIsCommanderSearchActive(false);
      }

      setSearchMode(newMode);
      refreshDeliveryStreams();
    },
    [searchMode, refreshDeliveryStreams]
  );

  // Generated by GitHub Copilot
  // Clear search and show all streams
  const clearSearch = useCallback(() => {
    setSearchTerm("");
    setSearchError("");
    setIsSearchActive(false);
    setCommanderSearchTerm("");
    setCommanderSearchError("");
    setIsCommanderSearchActive(false);
    setLeadershipSearchTerm("");
    setLeadershipSearchError("");
    setIsLeadershipSearchActive(false);
    refreshDeliveryStreams();
  }, [refreshDeliveryStreams]);

  // Generated by GitHub Copilot
  // Computed search statistics for mutually exclusive search with user-friendly messaging
  const searchStats = useMemo(() => {
    const hasActiveSearch =
      searchMode === "product"
        ? isSearchActive && searchTerm.trim()
        : searchMode === "commander"
        ? isCommanderSearchActive && commanderSearchTerm.trim()
        : isLeadershipSearchActive && leadershipSearchTerm.trim();

    if (!hasActiveSearch) {
      return null;
    }

    const activeSearchTerm =
      searchMode === "product"
        ? searchTerm.trim()
        : searchMode === "commander"
        ? commanderSearchTerm.trim()
        : leadershipSearchTerm.trim();

    return {
      searchMode,
      searchTerm: activeSearchTerm,
      resultCount: deliveryStreams.length,
      hasResults: deliveryStreams.length > 0,
      isEmpty: deliveryStreams.length === 0,
    };
  }, [
    searchMode,
    isSearchActive,
    searchTerm,
    isCommanderSearchActive,
    commanderSearchTerm,
    isLeadershipSearchActive,
    leadershipSearchTerm,
    deliveryStreams.length,
  ]);

  // Generated by GitHub Copilot
  // Handle filter toggle
  const toggleFilter = () => {
    const newFilterState = !showFilterBox;
    setShowFilterBox(newFilterState);

    // Track filter toggle
    trackDeliveryStreamOperation('FilterToggle', {
      filterVisible: newFilterState
    });

    // If hiding the filter box, clear any active search
    if (showFilterBox) {
      clearSearch();
    }
  };

  // Generated by GitHub Copilot
  // If user has no viewing privileges, show access denied
  if (!canViewDeliveryStreams) {
    return (
      <div className={styles.alertWarning}>
        <h3 className={styles.alertHeading}>Access Restricted</h3>
        <p className={styles.alertText}>
          You need configuration, editor, or administrator privileges to view
          delivery streams.
        </p>
        <hr />
        <p className={styles.alertText}>
          <small>
            Contact your administrator if you need access to this feature.
          </small>
        </p>
      </div>
    );
  }

  // Generated by GitHub Copilot
  // Loading state
  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <SkeletonLoader />
        <p className={styles.loadingText}>Loading delivery streams...</p>
      </div>
    );
  }

  // Generated by GitHub Copilot
  // Error state
  if (error) {
    return (
      <div className={styles.alertDanger}>
        <h3 className={styles.alertHeading}>Error Loading Delivery Streams</h3>
        <p className={styles.alertText}>{error}</p>
        <button
          className={`${styles.button} ${styles.buttonOutline}`}
          onClick={refreshDeliveryStreams}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div
      className={`${styles.container} ${
        isSearchActive || isCommanderSearchActive || isLeadershipSearchActive
          ? styles.searchActive
          : ""
      }`}
    >
      {/* Generated by GitHub Copilot */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>
            Delivery Stream Management
            {/* Generated by GitHub Copilot */}
            {!hasConfigurationPrivilege && (
              <span className={styles.badgeSecondary}>
                Read Only
              </span>
            )}
            {/* Generated by GitHub Copilot */}
            {searchStats && (
              <span className={styles.badgePrimary}>
                {searchStats.resultCount} result
                {searchStats.resultCount !== 1 ? "s" : ""}
              </span>
            )}
          </h1>
          <p className={styles.description}>
            {isSearchActive ||
            isCommanderSearchActive ||
            isLeadershipSearchActive ? (
              <>
                Showing delivery streams matching "{searchStats?.searchTerm}"
                {!hasConfigurationPrivilege && (
                  <>
                    {" "}
                    <small className={styles.warningText}>
                      <em>(Read Only)</em>
                    </small>
                  </>
                )}
              </>
            ) : (
              <>
                {hasConfigurationPrivilege
                  ? "Configure and manage delivery streams for incident management and escalation procedures."
                  : "View delivery streams for incident management and escalation procedures."}
                {/* Generated by GitHub Copilot */}
                {!hasConfigurationPrivilege && (
                  <>
                    <br />
                    <small className={styles.warningText}>
                      <em>Configuration privilege required for editing.</em>
                    </small>
                  </>
                )}
              </>
            )}
          </p>
        </div>
        <div className={styles.actions}>
          <RefreshIndicator
            isRefreshing={refreshing}
            lastUpdated={lastUpdated}
            showTimestamp={!refreshing && !!lastUpdated}
          />
          <button
            className={styles.buttonIcon + " " + styles.buttonOutline}
            onClick={refreshDeliveryStreams}
            title="Refresh delivery streams"
          >
            <FaSync />
          </button>
          {/* Generated by GitHub Copilot */}
          <button
            className={`${styles.buttonIcon} ${styles.buttonOutline} ${showFilterBox ? styles.buttonActive : ""}`}
            onClick={toggleFilter}
            title="Filter delivery streams"
          >
            <FaFilter />
          </button>
          {/* Generated by GitHub Copilot */}
          {hasConfigurationPrivilege && (
            <button
              className={`${styles.buttonIcon} ${styles.buttonPrimary}`}
              onClick={createNewDeliveryStream}
              title="New Delivery Stream"
            >
              <FaPlus />
            </button>
          )}
        </div>
      </div>

      {/* Generated by GitHub Copilot */}
      {/* Mutually Exclusive Search Interface */}
      {showFilterBox && (
        <div className={styles.searchContainer}>
          {/* Search Mode Selector */}
          <div className={styles.searchModeSelector}>
            <label className={styles.searchModeLabel}>Search Type:</label>
            <div className={styles.searchModes}>
              <div className={styles.radioGroup}>
                <input
                  type="radio"
                  id="search-mode-product"
                  name="searchMode"
                  className={styles.radioInput}
                  checked={searchMode === "product"}
                  onChange={() => handleSearchModeChange("product")}
                  disabled={loading}
                />
                <label htmlFor="search-mode-product" className={styles.radioLabel}>
                  Search by Product Name
                </label>
              </div>
              <div className={styles.radioGroup}>
                <input
                  type="radio"
                  id="search-mode-commander"
                  name="searchMode"
                  className={styles.radioInput}
                  checked={searchMode === "commander"}
                  onChange={() => handleSearchModeChange("commander")}
                  disabled={loading}
                />
                <label htmlFor="search-mode-commander" className={styles.radioLabel}>
                  Search by Incident Commander
                </label>
              </div>
              <div className={styles.radioGroup}>
                <input
                  type="radio"
                  id="search-mode-leadership"
                  name="searchMode"
                  className={styles.radioInput}
                  checked={searchMode === "leadership"}
                  onChange={() => handleSearchModeChange("leadership")}
                  disabled={loading}
                />
                <label htmlFor="search-mode-leadership" className={styles.radioLabel}>
                  Search by Leadership Responder
                </label>
              </div>
            </div>
          </div>

          {/* Product Search - Only show when product mode is active */}
          {searchMode === "product" && (
            <div className={styles.searchForm}>
              <form onSubmit={handleSearch}>
                <div className={`${styles.inputGroup} ${searchLoading ? styles.searchLoading : ""}`}>
                  <input
                    type="text"
                    className={styles.searchInput}
                    placeholder={`Search delivery streams by product name${
                      isSearchActive ? ` (${deliveryStreams.length} found)` : ""
                    }...`}
                    value={searchTerm}
                    onChange={handleSearchInputChange}
                    disabled={searchLoading || loading}
                    aria-label="Search delivery streams by product"
                  />
                  <div className={styles.searchButtons}>
                    <button
                      type="submit"
                      className={isSearchActive ? styles.searchButtonActive : styles.searchButton}
                      onClick={handleSearch}
                      disabled={searchLoading || loading}
                      title="Search delivery streams"
                      aria-label="Search"
                    >
                      {searchLoading ? (
                        <div className={styles.spinner} style={{ width: "16px", height: "16px" }} />
                      ) : (
                        <FaSearch />
                      )}
                    </button>
                    {(searchTerm || isSearchActive) && (
                      <button
                        type="button"
                        className={styles.searchButton}
                        onClick={clearSearch}
                        disabled={searchLoading || loading}
                        title="Clear search and show all delivery streams"
                        aria-label="Clear search"
                      >
                        <FaTimes />
                      </button>
                    )}
                  </div>
                </div>
              </form>

              {/* Generated by GitHub Copilot */}
              {/* Enhanced Search Status and Feedback with User-Friendly Empty Results */}
              {searchStats &&
                searchMode === "product" &&
                !searchLoading &&
                !searchError && (
                  <div className={styles.searchStatus}>
                    <small className={styles.searchStatusText}>
                      {searchStats.hasResults
                        ? `Found ${searchStats.resultCount} delivery stream${
                            searchStats.resultCount !== 1 ? "s" : ""
                          } containing product "${searchStats.searchTerm}"`
                        : `No delivery streams contain product "${searchStats.searchTerm}". Try a different product name or check spelling.`}
                    </small>
                    {searchStats.hasResults && (
                      <small className={styles.searchStatusIndicator}>
                        Real-time search enabled
                      </small>
                    )}
                  </div>
                )}

              {searchError && (
                <div className={styles.alertWarning}>
                  <div className={styles.alertHeader}>
                    <small className={styles.alertText}>{searchError}</small>
                    <button
                      className={styles.alertDismiss}
                      onClick={() => setSearchError("")}
                      aria-label="Dismiss"
                    >
                      <FaTimes />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Commander Search - Only show when commander mode is active */}
          {searchMode === "commander" && (
            <div className={styles.searchForm}>
              <form onSubmit={handleSearch}>
                <div className={`${styles.inputGroup} ${commanderSearchLoading ? styles.searchLoading : ""}`}>
                  <input
                    type="text"
                    className={styles.searchInput}
                    placeholder={`Search delivery streams by incident commander name${
                      isCommanderSearchActive
                        ? ` (${deliveryStreams.length} found)`
                        : ""
                    }...`}
                    value={commanderSearchTerm}
                    onChange={handleCommanderSearchInputChange}
                    disabled={commanderSearchLoading || loading}
                    aria-label="Search delivery streams by incident commander"
                  />
                  <div className={styles.searchButtons}>
                    <button
                      type="submit"
                      className={isCommanderSearchActive ? styles.searchButtonActive : styles.searchButton}
                      onClick={handleSearch}
                      disabled={commanderSearchLoading || loading}
                      title="Search delivery streams by commander"
                      aria-label="Search"
                    >
                      {commanderSearchLoading ? (
                        <div className={styles.spinner} style={{ width: "16px", height: "16px" }} />
                      ) : (
                        <FaSearch />
                      )}
                    </button>
                    {(commanderSearchTerm || isCommanderSearchActive) && (
                      <button
                        type="button"
                        className={styles.searchButton}
                        onClick={clearSearch}
                        disabled={commanderSearchLoading || loading}
                        title="Clear commander search"
                        aria-label="Clear commander search"
                      >
                        <FaTimes />
                      </button>
                    )}
                  </div>
                </div>
              </form>

              {/* Commander Search Feedback with User-Friendly Empty Results */}
              {searchStats &&
                searchMode === "commander" &&
                !commanderSearchLoading &&
                !commanderSearchError && (
                  <div className={styles.searchStatus}>
                    <small className={styles.searchStatusText}>
                      {searchStats.hasResults
                        ? `Found ${searchStats.resultCount} delivery stream${
                            searchStats.resultCount !== 1 ? "s" : ""
                          } with commander "${searchStats.searchTerm}"`
                        : `No delivery streams have commander "${searchStats.searchTerm}". Try a different commander name or check spelling.`}
                    </small>
                    {searchStats.hasResults && (
                      <small className={styles.searchStatusIndicator}>
                        Real-time search enabled
                      </small>
                    )}
                  </div>
                )}

              {/* Commander Search Feedback */}
              {commanderSearchError && (
                <div className={styles.alertWarning}>
                  <div className={styles.alertHeader}>
                    <small className={styles.alertText}>{commanderSearchError}</small>
                    <button
                      className={styles.alertDismiss}
                      onClick={() => setCommanderSearchError("")}
                      aria-label="Dismiss"
                    >
                      <FaTimes />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Leadership Search - Only show when leadership mode is active */}
          {searchMode === "leadership" && (
            <div className={styles.searchForm}>
              <form onSubmit={handleSearch}>
                <div className={`${styles.inputGroup} ${leadershipSearchLoading ? styles.searchLoading : ""}`}>
                  <input
                    type="text"
                    className={styles.searchInput}
                    placeholder={`Search delivery streams by leadership responder name${
                      isLeadershipSearchActive
                        ? ` (${deliveryStreams.length} found)`
                        : ""
                    }...`}
                    value={leadershipSearchTerm}
                    onChange={handleLeadershipSearchInputChange}
                    disabled={leadershipSearchLoading || loading}
                    aria-label="Search delivery streams by leadership responder"
                  />
                  <div className={styles.searchButtons}>
                    <button
                      type="submit"
                      className={isLeadershipSearchActive ? styles.searchButtonActive : styles.searchButton}
                      onClick={handleSearch}
                      disabled={leadershipSearchLoading || loading}
                      title="Search delivery streams by leadership responder"
                      aria-label="Search"
                    >
                      {leadershipSearchLoading ? (
                        <div className={styles.spinner} style={{ width: "16px", height: "16px" }} />
                      ) : (
                        <FaSearch />
                      )}
                    </button>
                    {(leadershipSearchTerm || isLeadershipSearchActive) && (
                      <button
                        type="button"
                        className={styles.searchButton}
                        onClick={clearSearch}
                        disabled={leadershipSearchLoading || loading}
                        title="Clear leadership search"
                        aria-label="Clear leadership search"
                      >
                        <FaTimes />
                      </button>
                    )}
                  </div>
                </div>
              </form>

              {/* Leadership Search Feedback with User-Friendly Empty Results */}
              {searchStats &&
                searchMode === "leadership" &&
                !leadershipSearchLoading &&
                !leadershipSearchError && (
                  <div className={styles.searchStatus}>
                    <small className={styles.searchStatusText}>
                      {searchStats.hasResults
                        ? `Found ${searchStats.resultCount} delivery stream${
                            searchStats.resultCount !== 1 ? "s" : ""
                          } with leadership responder "${
                            searchStats.searchTerm
                          }"`
                        : `No delivery streams have leadership responder "${searchStats.searchTerm}". Try a different responder name or check spelling.`}
                    </small>
                    {searchStats.hasResults && (
                      <small className={styles.searchStatusIndicator}>
                        Real-time search with PagerDuty integration
                      </small>
                    )}
                  </div>
                )}

              {/* Leadership Search Error Feedback */}
              {leadershipSearchError && (
                <div className={styles.alertWarning}>
                  <div className={styles.alertHeader}>
                    <small className={styles.alertText}>{leadershipSearchError}</small>
                    <button
                      className={styles.alertDismiss}
                      onClick={() => setLeadershipSearchError("")}
                      aria-label="Dismiss"
                    >
                      <FaTimes />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Generated by GitHub Copilot */}
      {/* Table for desktop */}
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Delivery Stream</th>
            <th>Portfolio</th>
            {hasConfigurationPrivilege && <th></th>}
          </tr>
        </thead>
        <tbody>
          {definitions && definitions.length > 0 ? (
            definitions
          ) : (
            <tr key="none">
              <td colSpan={hasConfigurationPrivilege ? "3" : "2"} className={styles.tableCell} style={{ textAlign: "center", color: "var(--text-secondary)", padding: "var(--space-xl)" }}>
                {searchStats
                  ? searchMode === "product"
                    ? `No delivery streams contain product "${searchStats.searchTerm}"`
                    : searchMode === "commander"
                    ? `No delivery streams have commander "${searchStats.searchTerm}"`
                    : `No delivery streams have leadership responder "${searchStats.searchTerm}"`
                  : "No Delivery Streams Currently Defined"}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Generated by GitHub Copilot */}
      {/* Card list for mobile */}
      <div className={styles.cardList}>
        {deliveryStreams && deliveryStreams.length > 0 ? (
          deliveryStreams.map((def) => {
            const defLink = "/configuration/deliverystream/" + def.id;
            return (
              <div key={def.id} className={styles.card}>
                <div className={styles.cardHeader}>
                  {canViewDeliveryStreams ? (
                    <Link to={defLink} className={styles.cardTitle}>
                      {def.name}
                    </Link>
                  ) : (
                    <span className={styles.cardTitle}>{def.name}</span>
                  )}
                  {!hasConfigurationPrivilege && (
                    <span className={styles.badgeSecondary}>Read Only</span>
                  )}
                </div>
                <div className={styles.cardBody}>
                  <div className={styles.cardField}>
                    <span className={styles.cardLabel}>Portfolio:</span>
                    <span className={styles.cardValue}>{def.portfolio}</span>
                  </div>
                  {def._leadershipSearchData && (
                    <div className={styles.cardField}>
                      <span className={styles.cardLabel}>Workflows:</span>
                      <span className={styles.cardValue}>
                        Found in {def._leadershipSearchData.matchedWorkflows.length} workflow
                        {def._leadershipSearchData.matchedWorkflows.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                  )}
                </div>
                {hasConfigurationPrivilege && (
                  <div className={styles.cardActions}>
                    <button
                      className={`${styles.cardButton} ${styles.cardButtonPrimary}`}
                      onClick={() => navigate(defLink)}
                      aria-label={`Edit ${def.name}`}
                    >
                      <FaEdit /> Edit
                    </button>
                    <button
                      className={`${styles.cardButton} ${styles.cardButtonDanger}`}
                      onClick={() => { setStreamToDelete(def); setShowDeleteModal(true); }}
                      aria-label={`Delete ${def.name}`}
                    >
                      <FaTrash /> Delete
                    </button>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className={styles.emptyState}>
            {searchStats
              ? searchMode === "product"
                ? `No delivery streams contain product "${searchStats.searchTerm}"`
                : searchMode === "commander"
                ? `No delivery streams have commander "${searchStats.searchTerm}"`
                : `No delivery streams have leadership responder "${searchStats.searchTerm}"`
              : "No Delivery Streams Currently Defined"}
          </div>
        )}
      </div>

      {/* Generated by GitHub Copilot */}
      {deliveryStreams.length > 0 && (
        <div style={{ marginTop: "var(--space-md)" }}>
          <small style={{ color: "var(--text-secondary)" }}>
            Total: {deliveryStreams.length} delivery stream
            {deliveryStreams.length !== 1 ? "s" : ""}
          </small>
        </div>
      )}

      {/* Generated by GitHub Copilot */}
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setStreamToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Delete Delivery Stream"
        message={`Are you sure you want to delete "${streamToDelete?.name || 'this delivery stream'}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
}

export default DeliveryStreamsList;
